/**
 * AWP Variant Price + Media Sync
 *
 * The PDP option widgets (blocks/awp_g_size_selector.liquid and
 * blocks/awp_grind_selector.liquid) resolve a variant client-side and announce
 * it with `awp:variant-change`. They replace the native <variant-picker>, which
 * is what normally re-renders <product-price> and re-selects <media-gallery>'s
 * slideshow — so without this, the price and the gallery image keep showing
 * whatever variant the server rendered and never follow a size/grind change.
 *
 * Price: ask the Section Rendering API for the section at the chosen variant
 * and swap the price container in, the same way the native picker does. Money
 * formatting, compare-at price and unit price stay server-authoritative
 * instead of being rebuilt in JS.
 *
 * Media: every slide already carries its Shopify media id as `slide-id`
 * (blocks/_product-media-gallery.liquid renders `slide_id: media.id`), and the
 * event carries the target variant's `featured_media.id` directly — so this
 * calls the gallery's own Slideshow#select() with that id, the exact method
 * slideshow-arrows.js uses, so it animates the same way arrow navigation does.
 */

/** In-flight request per price block, so rapid clicks can't land out of order. @type {Map<string, AbortController>} */
const pendingRequests = new Map();

/**
 * @param {Element} element
 * @returns {string | null} The section id without the `shopify-section-` prefix.
 */
function sectionIdOf(element) {
  const section = element.closest('.shopify-section');
  if (!(section instanceof HTMLElement) || !section.id) return null;
  return section.id.replace(/^shopify-section-/, '');
}

/**
 * Price blocks on the page showing this product. Product cards are excluded —
 * they render their own price for their own selection.
 * @param {string} productId
 * @returns {HTMLElement[]}
 */
function priceElementsFor(productId) {
  /** @type {HTMLElement[]} */
  const elements = [];

  for (const element of document.querySelectorAll('product-price[data-product-id]')) {
    if (!(element instanceof HTMLElement)) continue;
    if (element.dataset.productId !== productId) continue;
    if (element.closest('product-card')) continue;
    elements.push(element);
  }

  return elements;
}

/**
 * Animates every on-page <media-gallery> to the slide matching featuredMediaId,
 * the same way clicking a slideshow arrow does. No-op if the gallery has no
 * slide for that media id (e.g. the variant has no featured media of its own).
 * @param {string} featuredMediaId
 */
function animateMediaGalleries(featuredMediaId) {
  for (const gallery of document.querySelectorAll('media-gallery')) {
    const slideshow = /** @type {any} */ (gallery).slideshow;
    if (!slideshow || typeof slideshow.select !== 'function') continue;
    slideshow.select({ id: featuredMediaId }, undefined, { animate: true });
  }
}

/**
 * @param {HTMLElement} priceElement
 * @param {string} variantId
 */
function refreshPrice(priceElement, variantId) {
  const sectionId = sectionIdOf(priceElement);
  if (!sectionId) return;

  const blockId = priceElement.dataset.blockId || '';
  const key = `${sectionId}:${blockId}`;

  pendingRequests.get(key)?.abort();
  const controller = new AbortController();
  pendingRequests.set(key, controller);

  const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&variant=${encodeURIComponent(
    variantId
  )}`;

  fetch(url, { signal: controller.signal })
    .then((response) => response.text())
    .then((responseText) => {
      pendingRequests.delete(key);

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selector = blockId
        ? `product-price[data-block-id="${blockId}"] [ref="priceContainer"]`
        : 'product-price [ref="priceContainer"]';

      const newPrice = html.querySelector(selector);
      const currentPrice = priceElement.querySelector('[ref="priceContainer"]');

      if (newPrice && currentPrice && currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.replaceWith(newPrice);
      }
    })
    .catch((error) => {
      if (error.name === 'AbortError') return;
      console.error('AWP Variant Price Sync: failed to refresh price', error);
    });
}

document.addEventListener('awp:variant-change', (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (!detail || detail.variantId == null) return;

  const productId = String(detail.productId);
  const variantId = String(detail.variantId);

  for (const priceElement of priceElementsFor(productId)) {
    refreshPrice(priceElement, variantId);
  }

  if (detail.featuredMediaId != null) {
    animateMediaGalleries(String(detail.featuredMediaId));
  }
});
